import { useEffect, useRef, useCallback } from 'react';

import loader from '@ibsheet/loader';

import { Default_Cfg, Default_Editable_Def, Default_UnEditable_Def, Left_Presets } from '@bo-ibSheet/common';

const useIBSheet = (
  unPagedCfg,
  readOnly,
  optionsProp,
  onDataChange,
  handleSortChange,
  grid,
  unLoaded,
  setCheckedRows,
  handlePageableSearch,
  sheetEl,
  data,
  renderSafeJobs,
  methodProps
) => {
  const {
    onRenderFirstFinish,
    onBeforeDataLoad,
    onDataLoad,
    onSearchFinish,
    onAfterChange,
    onCheckboxAfterChange,
    onClick,
    onBlur,
    onFocus,
    onBeforeChange,
    onButtonClick,
    onIconClick,
    onResultMask,
    onRowAdd,
    onEndEdit,
    onShowEnumMenu,
    onSelectFile,
    onImportFinish,
    onAfterRowDelete,
  } = methodProps;

  const isRendered = useCallback(() => !!document.getElementById(sheetEl.current), [sheetEl]);

  useEffect(() => {
    const { Cfg = {}, Def = {}, LeftCols = [...Left_Presets], RightCols = [], Cols, Foot = [] } = optionsProp;
    const defaultDef = readOnly ? { ...Default_UnEditable_Def } : { ...Default_Editable_Def };
    const defaultCfg = { ...Default_Cfg, CanEdit: readOnly ? 0 : 1 };

    const options = {
      Cfg: { ...defaultCfg, ...unPagedCfg, ...Cfg },
      Def: { ...defaultDef, ...Def },
      LeftCols,
      RightCols,
      Cols,
      Foot,
      Events: {
        ...IBSheetMethods(
          {
            onBeforeSort: handleSortChange,
            onDataChange,
            onRenderFirstFinish,
            onBeforeDataLoad,
            onDataLoad,
            onSearchFinish,
            onAfterChange,
            onCheckboxAfterChange,
            onClick,
            onBlur,
            onFocus,
            onBeforeChange,
            onButtonClick,
            onIconClick,
            onResultMask,
            onRowAdd,
            onEndEdit,
            onShowEnumMenu,
            onSelectFile,
            onImportFinish,
            onAfterRowDelete,
          },
          grid,
          {
            unLoaded,
            setCheckedRows,
            handlePageableSearch,
            renderSafeJobs,
            setRenderFinished,
          }
        ),
      },
    };

    const crateSheet = () => {
      loader
        .createSheet({
          el: sheetEl.current,
          options,
          data: [],
          ...(grid.current?.id ? { id: grid.current?.id } : {}),
        })
        .then((sheet) => {
          // 주의사항: 해당 구간에서 데이터 조회를 하면 안됩니다.
          if (grid.current && sheet?.id !== grid.current?.id && isRendered()) {
            loader.removeSheet(grid.current?.id);
          } else {
            grid.current = sheet;
          }
        });
    };

    let intervalId;
    const tenaciousSheetCreator = (legacyGridId) => {
      const clear = () => {
        clearInterval(intervalId);
        intervalId = undefined;
      };

      if (isRendered()) {
        intervalId && clear();
        legacyGridId === grid.current?.id && crateSheet();
      }
    };

    if (isRendered()) {
      crateSheet();
    } else {
      intervalId = setInterval(tenaciousSheetCreator, 500, grid.current?.id);
    }

    return () => {
      isRendered() && loader.removeSheet(grid.current?.id);
    };
  }, [
    grid,
    optionsProp,
    readOnly,
    setCheckedRows,
    sheetEl,
    unLoaded,
    unPagedCfg,
    handlePageableSearch,
    handleSortChange,
    onAfterChange,
    onCheckboxAfterChange,
    onBeforeChange,
    onBeforeDataLoad,
    onBlur,
    onButtonClick,
    onClick,
    onDataChange,
    onDataLoad,
    onEndEdit,
    onFocus,
    onIconClick,
    onImportFinish,
    onRenderFirstFinish,
    onResultMask,
    onRowAdd,
    onSearchFinish,
    onSelectFile,
    onShowEnumMenu,
    renderSafeJobs,
    onAfterRowDelete,
    isRendered,
  ]);

  const renderFinished = useRef(false);
  const setRenderFinished = () => {
    renderFinished.current = true;
  };

  useEffect(() => {
    const clear = () => {
      grid.current?.setAllCheck?.('CHK', false, true);

      setCheckedRows?.([]);
      onDataChange?.(false);
    };

    if (renderFinished.current) {
      grid.current?.loadSearchData?.({ data, callback: clear });
    }
  }, [data, grid, onDataChange, setCheckedRows]);
};

export const IBSheetMethods = (
  methodProps,
  grid,
  { unLoaded, setCheckedRows, handlePageableSearch, renderSafeJobs, setRenderFinished }
) => {
  const {
    onRenderFirstFinish: onRenderFirstFinishProp,
    onBeforeDataLoad: onBeforeDataLoadProp,
    onDataLoad: onDataLoadProp,
    onSearchFinish: onSearchFinishProp,
    onAfterChange: onAfterChangeProp,
    onCheckboxAfterChange: onCheckboxAfterChangeProp,
    onClick: onClickProp,
    onBlur: onBlurProp,
    onFocus: onFocusProp,
    onBeforeChange: onBeforeChangeProp,
    onButtonClick: onButtonClickProp,
    onIconClick: onIconClickProp,
    onResultMask: onResultMaskProp,
    onRowAdd: onRowAddProp,
    onEndEdit: onEndEditProp,
    onShowEnumMenu: onShowEnumMenuProp,
    onBeforeSort: onBeforeSortProp,
    onSelectFile: onSelectFileProp,
    onImportFinish: onImportFinishProp,
    onAfterRowDelete: onAfterRowDeleteProp,
    onDataChange,
  } = methodProps;
  const onRenderFirstFinish = (event) => {
    grid.current = event.sheet;

    const finishJob = () => {
      setRenderFinished();
      onRenderFirstFinishProp?.(event);
      while (renderSafeJobs?.()?.length > 0 && !unLoaded) {
        renderSafeJobs?.()?.shift()(grid.current);
      }
    };

    if (unLoaded) {
      event.sheet.loadSearchData({ data: [], callback: finishJob });
    } else {
      handlePageableSearch();
      finishJob();
    }
  };
  const onBeforeDataLoad = (event) => {
    onBeforeDataLoadProp?.(event);
  };
  const onDataLoad = (event) => {
    onDataLoadProp?.(event);
  };
  const onSearchFinish = (event) => {
    onSearchFinishProp?.(event);
  };
  const onBeforeChange = (event) => {
    return onBeforeChangeProp?.(event);
  };
  const onAfterChange = (event) => {
    onDataChange?.(event?.sheet?.hasChangedData() > 0);
    if (event?.col === 'CHK') {
      setCheckedRows(event.sheet.getRowsByChecked('CHK'));
      onCheckboxAfterChangeProp?.(event);
    } else {
      onAfterChangeProp?.(event);
    }
  };
  const onClick = (event) => {
    onClickProp && onClickProp(event);
  };
  const onBlur = (event) => {
    onBlurProp?.(event);
  };
  const onFocus = (event) => {
    onFocusProp?.(event);
  };
  const onButtonClick = (event) => {
    onButtonClickProp?.(event);
  };
  const onIconClick = (event) => {
    onIconClickProp?.(event);
  };
  const onRowAdd = (event) => {
    onRowAddProp?.(event);
  };
  const onShowEnumMenu = (event) => {
    return onShowEnumMenuProp?.(event);
  };
  const onEndEdit = (event) => {
    return event?.save === 1 && onEndEditProp ? onEndEditProp(event) : event?.val;
  };
  const onResultMask = (event) => {
    onResultMaskProp?.(event);

    return 4;
  };
  const onBeforeSort = (event) => {
    if (event?.sheet?.HeaderSortMode < 2) return;

    const generateParameter = (event) => {
      const { sort = '' } = event;
      if (sort === '') return;

      const order = sort?.startsWith('-') ? 'desc' : 'asc';
      const key = event.sheet.Cols[order === 'desc' ? sort.slice(1) : sort].Key;
      return { key, order };
    };

    return event?.sheet?.getDataRows().length > 0 && onBeforeSortProp?.(generateParameter(event)) ? 1 : -1;
  };

  const onSelectFile = (event) => {
    event.sheet.setValue(event.sheet.getRowById('Header'), 'ErrorDescription', '', 0);

    onSelectFileProp?.(event);
  };

  const onImportFinish = (event) => {
    event.sheet.setValue(event.sheet.getRowById('Header'), 'ErrorDescription', '오류내역');

    return onImportFinishProp?.(event);
  };

  const onStartDrag = (event) => {
    return event.col !== 'DragAndDropIcon';
  };

  const onAfterRowDelete = (event) => {
    onAfterRowDeleteProp?.(event);
  };

  const onRightClick = () => true;

  return {
    onRenderFirstFinish,
    onBeforeDataLoad,
    onDataLoad,
    onSearchFinish,
    onAfterChange,
    onClick,
    onBlur,
    onFocus,
    onButtonClick,
    onIconClick,
    onBeforeChange,
    onRowAdd,
    onShowEnumMenu,
    onEndEdit,
    onResultMask,
    onBeforeSort,
    onSelectFile,
    onImportFinish,
    onStartDrag,
    onAfterRowDelete,
    onRightClick,
  };
};

export default useIBSheet;
